import type { EventRelative } from '@goaltree/backend/convex/lib/events'
import {
  type TravelEstimate,
  friendlyDistance,
  usesMiles,
} from '@goaltree/backend/convex/lib/travel'
import type { TFunction } from 'i18next'

const pad = (n: number) => String(n).padStart(2, '0')

export function hhmm(ms: number) {
  const d = new Date(ms)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function startOfLocalDay(ms: number) {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

export const addLocalDays = (ms: number, n: number) => {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n).getTime()
}

export const localNoonOf = (ms: number) => startOfLocalDay(ms) + 12 * 3_600_000

export function dayKeyOf(ms: number) {
  const d = new Date(ms)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// "YYYY-MM-DD" + optional "HH:MM" → local epoch ms (midnight when no time).
export function localDateTime(day: string, time?: string | null) {
  const [y, m, d] = day.split('-').map(Number)
  const [h, min] = time ? time.split(':').map(Number) : [0, 0]
  return new Date(y, m - 1, d, h, min).getTime()
}

// 45 → "45m", 60 → "1h", 90 → "1h 30m" (localized units).
export function durationLabel(minutes: number, t: TFunction) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (!h) return t('events.durMin', { n: m })
  if (!m) return t('events.durHour', { n: h })
  return t('events.durHourMin', { h, m })
}

export function relativeLabel(rel: EventRelative, t: TFunction) {
  switch (rel.kind) {
    case 'done':
      return t('events.done')
    case 'allDay':
      return t('events.allDay')
    case 'now':
      return t('events.nowLeft', { left: durationLabel(rel.minutesLeft, t) })
    case 'in':
      return t('events.inTime', { time: durationLabel(rel.minutes, t) })
  }
}

// "~6 km · about 15 min by car" — always hedged; distance only when far.
export function travelLabel(travel: TravelEstimate, t: TFunction, locale = navigator.language) {
  const { value, unit } = friendlyDistance(travel.km, usesMiles(locale))
  const distance = t(unit === 'mi' ? 'events.travelMi' : 'events.travelKm', {
    n: value.toLocaleString(locale),
  })
  if (travel.minutes == null || !travel.mode) return distance
  const time =
    travel.minutes === 0
      ? t(travel.mode === 'walk' ? 'events.walkUnder5' : 'events.driveUnder5')
      : t(travel.mode === 'walk' ? 'events.walkAbout' : 'events.driveAbout', { n: travel.minutes })
  return `${distance} · ${time}`
}

// "Thu, Oct 29" (year only when not this one).
export function dayLabel(ms: number, locale: string) {
  const d = new Date(ms)
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== new Date().getFullYear() && { year: 'numeric' }),
  })
}

type Span = { start: number; end: number }

const TEN_MIN = 10 * 60_000

// A Google busy block that is really a Goaltree event synced to the calendar.
export function duplicatesEvent(busy: Span, event: Span) {
  const busyLen = busy.end - busy.start
  if (busyLen <= 0) return false
  const overlap = Math.min(busy.end, event.end) - Math.max(busy.start, event.start)
  if (overlap >= 0.8 * busyLen) return true
  const eventLen = event.end - event.start
  return (
    Math.abs(busy.start - event.start) <= TEN_MIN &&
    Math.abs(busyLen - eventLen) <= Math.max(TEN_MIN, 0.2 * Math.max(busyLen, eventLen))
  )
}

export function dedupeBusy<B extends Span>(busy: B[], events: Span[]) {
  return busy.filter((b) => !events.some((e) => duplicatesEvent(b, e)))
}
